/**
 * Daily nutrient-budget calc — mirrors functions/src/utils/thresholds.ts and
 * the canonical spec (docs/LIVER_MODEL_SPEC.md §3). Divergence here CORRUPTS
 * the shared healthProfile.calculatedLimits every platform consumes.
 *
 * Ceilings: calories, added sugar, saturated fat, sodium.
 * Floors/targets: fiber ("aim for") and protein (clinical target for
 * cirrhosis / alcohol-related — never a ceiling in the liver model).
 */

export interface NutrientLimitResult {
  calories: number
  addedSugarG: number
  saturatedFatG: number
  fiberG: number
  sodiumMg: number
  proteinPerKg: number
  proteinIsTarget: boolean
}

// Condition picker set (spec §1) — the exact editor set, so a condition chosen
// on either platform round-trips on both.
export const LIVER_CONDITIONS: { value: string; labelKey: string; fallback: string }[] = [
  { value: 'fatty_liver', labelKey: 'settings.conditionStages.fattyLiver', fallback: 'Fatty Liver (MASLD/NAFLD)' },
  { value: 'mash', labelKey: 'settings.conditionStages.mash', fallback: 'MASH (NASH)' },
  { value: 'fibrosis', labelKey: 'settings.conditionStages.fibrosis', fallback: 'Liver Fibrosis (F2–F3)' },
  { value: 'cirrhosis', labelKey: 'settings.conditionStages.cirrhosis', fallback: 'Cirrhosis (compensated)' },
  { value: 'cirrhosis_decompensated', labelKey: 'settings.conditionStages.cirrhosisDecompensated', fallback: 'Cirrhosis (decompensated)' },
  { value: 'alcohol_related', labelKey: 'settings.conditionStages.alcoholRelated', fallback: 'Alcohol-related liver disease' },
  { value: 'not_sure_prevention', labelKey: 'settings.conditionStages.prevention', fallback: 'Not sure / prevention' },
  { value: 'none', labelKey: 'settings.conditionStages.noDiagnosis', fallback: 'No diagnosis' },
]

// Comorbidity set (spec §2).
export const COMORBIDITIES: { value: string; labelKey: string; fallback: string }[] = [
  { value: 'type_2_diabetes', labelKey: 'settings.comorbidities.type2Diabetes', fallback: 'Type 2 diabetes' },
  { value: 'high_cholesterol', labelKey: 'settings.comorbidities.highCholesterol', fallback: 'High cholesterol' },
  { value: 'high_blood_pressure', labelKey: 'settings.comorbidities.highBloodPressure', fallback: 'High blood pressure' },
  { value: 'heart_disease', labelKey: 'settings.comorbidities.heartDisease', fallback: 'Heart disease' },
  { value: 'obesity', labelKey: 'settings.comorbidities.obesity', fallback: 'Obesity' },
  { value: 'hypothyroidism', labelKey: 'settings.comorbidities.hypothyroidism', fallback: 'Hypothyroidism' },
]

/** Map a stored condition value to its translation key. */
export function conditionLabelKey(condition?: string): { key: string; fallback: string } {
  const found = LIVER_CONDITIONS.find((c) => c.value === condition)
  if (found) return { key: found.labelKey, fallback: found.fallback }
  return { key: 'settings.conditionStages.prevention', fallback: condition || '—' }
}

const DEFAULT_WEIGHT_KG = 70

/** Weight sanity window 20–300kg (mirrors thresholds.ts safeWeight): out of range → 70. */
function safeWeight(weightKg?: number): number {
  if (typeof weightKg !== 'number' || !Number.isFinite(weightKg) || weightKg <= 20 || weightKg > 300) {
    return DEFAULT_WEIGHT_KG
  }
  return weightKg
}

/**
 * Spec §3 table. Calories: the weight-loss deficit (max(1400, 30×kg − 600)) is
 * the default for fatty_liver/mash/fibrosis because 7–10% weight loss is the
 * primary treatment; cirrhosis (both stages) gets 32×kg with NO deficit —
 * cirrhosis patients must never be pushed toward catabolism/sarcopenia.
 * Comorbidity adjustments apply AFTER the base table; satFatG is derived from
 * the FINAL calories (round(calories × pct / 9)).
 */
export function calculateNutrientLimits(
  condition: string,
  comorbidities: string[] = [],
  weightKg?: number,
  _sex?: 'male' | 'female',
): NutrientLimitResult {
  const kg = safeWeight(weightKg)
  const deficitCalories = Math.max(1400, Math.round(30 * kg - 600))
  const maintenanceCalories = Math.round(30 * kg)
  const cirrhosisCalories = Math.round(32 * kg)

  let calories: number
  let addedSugarG: number
  let satFatPct: number
  let fiberG: number
  let sodiumMg: number
  let proteinPerKg: number
  let proteinIsTarget = false
  // Cirrhosis (compensated or decompensated) must never get a calorie deficit,
  // even with the obesity comorbidity.
  let allowObesityDeficit = true

  switch (condition) {
    case 'fatty_liver':
      calories = deficitCalories
      addedSugarG = 30
      satFatPct = 0.10
      fiberG = 28
      sodiumMg = 2300
      proteinPerKg = 1.0
      break
    case 'mash':
    case 'fibrosis':
      calories = deficitCalories
      addedSugarG = 25
      satFatPct = 0.07
      fiberG = 30
      sodiumMg = 2300
      proteinPerKg = 1.0
      break
    case 'cirrhosis':
      calories = cirrhosisCalories
      addedSugarG = 30
      satFatPct = 0.10
      fiberG = 28
      sodiumMg = 2000
      proteinPerKg = 1.35
      proteinIsTarget = true
      allowObesityDeficit = false
      break
    case 'cirrhosis_decompensated':
      calories = cirrhosisCalories
      addedSugarG = 30
      satFatPct = 0.10
      fiberG = 25
      sodiumMg = 2000
      proteinPerKg = 1.35
      proteinIsTarget = true
      allowObesityDeficit = false
      break
    case 'alcohol_related':
      // Maintenance by default (malnutrition is common); deficit only with obesity.
      calories = maintenanceCalories
      addedSugarG = 30
      satFatPct = 0.10
      fiberG = 28
      sodiumMg = 2300
      proteinPerKg = 1.35
      proteinIsTarget = true
      break
    case 'not_sure_prevention':
    case 'none':
    default:
      calories = maintenanceCalories
      addedSugarG = 36
      satFatPct = 0.10
      fiberG = 28
      sodiumMg = 2300
      proteinPerKg = 0.8
  }

  // Comorbidity adjustments AFTER the base table (spec §2).
  if (comorbidities.includes('type_2_diabetes')) addedSugarG = Math.min(addedSugarG, 25)
  if (comorbidities.includes('high_cholesterol') || comorbidities.includes('heart_disease')) satFatPct = 0.07
  if (comorbidities.includes('high_blood_pressure')) sodiumMg -= 300
  if (comorbidities.includes('heart_disease')) sodiumMg -= 300 // stacks with hypertension
  sodiumMg = Math.max(1500, sodiumMg)
  if (comorbidities.includes('obesity') && allowObesityDeficit) calories = deficitCalories
  // hypothyroidism: no numeric change (noted in AI context only)

  return {
    calories,
    addedSugarG,
    saturatedFatG: Math.round((calories * satFatPct) / 9),
    fiberG,
    sodiumMg,
    proteinPerKg,
    proteinIsTarget,
  }
}
